import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Clock, Truck, Settings, ExternalLink,
  LayoutDashboard, Building2, MessageCircle, BarChart3,
  ChevronRight, Edit, Globe, CheckCircle2, AlertCircle,
  Loader2, LogOut, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalG2 = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platformStatus, setPlatformStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmpresas: 0,
    totalUsuarios: 0,
    mensagensNaoLidas: 0
  });

  // Configuração das plataformas
  const [platforms, setPlatforms] = useState([
    {
      id: 'g2egente',
      name: 'G2EGENTE',
      description: 'Gestão de Pessoas e RH',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      url: 'https://www.g2egente.com.br',
      adminPath: '/admin',
      features: ['Gestão de colaboradores', 'Folha de pagamento', 'Benefícios', 'Recrutamento'],
      active: true
    },
    {
      id: 'jornyx',
      name: 'JORNYX',
      description: 'Gestão de Jornada de Trabalho',
      icon: Clock,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      url: window.location.origin,
      adminPath: '/plataforma',
      features: ['Ponto eletrônico', 'Banco de horas', 'Escalas', 'Ocorrências'],
      active: true,
      isCurrentPlatform: true
    },
    {
      id: 'g2trucker',
      name: 'G2TRUCKER',
      description: 'Gestão de Frotas e Motoristas',
      icon: Truck,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      url: 'https://g2trucker.com.br',
      adminPath: '/dashboard/super-admin',
      features: ['Controle de frota', 'Jornada motorista', 'Manutenção', 'Roteirização'],
      active: true
    }
  ]);

  useEffect(() => {
    loadPlatformConfigs();
    loadStats();
    checkPlatformStatus();
  }, []);

  const loadPlatformConfigs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/g2-portal/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const configs = await response.json();
        if (configs && configs.platforms) {
          setPlatforms(prev => prev.map(p => {
            const saved = configs.platforms.find(c => c.id === p.id);
            return saved ? { ...p, url: saved.url || '', active: saved.active !== false } : p;
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Carregar estatísticas do Jornyx
      const [empresasRes, mensagensRes] = await Promise.all([
        fetch(`${API_URL}/api/empresas`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/chat/nao-lidas`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (empresasRes.ok) {
        const empresas = await empresasRes.json();
        setStats(prev => ({ ...prev, totalEmpresas: empresas.length }));
      }

      if (mensagensRes.ok) {
        const mensagens = await mensagensRes.json();
        setStats(prev => ({ ...prev, mensagensNaoLidas: mensagens.total_nao_lidas || 0 }));
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const checkPlatformStatus = async () => {
    const status = {};
    for (const platform of platforms) {
      if (platform.url && !platform.isCurrentPlatform) {
        try {
          // Tentar fazer um health check básico
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          await fetch(platform.url, { 
            mode: 'no-cors',
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          status[platform.id] = 'online';
        } catch (error) {
          status[platform.id] = 'offline';
        }
      } else if (platform.isCurrentPlatform) {
        status[platform.id] = 'online';
      } else {
        status[platform.id] = 'not-configured';
      }
    }
    setPlatformStatus(status);
  };

  const savePlatformConfig = async (platformId, url) => {
    try {
      const updatedPlatforms = platforms.map(p => 
        p.id === platformId ? { ...p, url } : p
      );

      const response = await fetch(`${API_URL}/api/g2-portal/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          platforms: updatedPlatforms.map(p => ({
            id: p.id,
            url: p.url,
            active: p.active
          }))
        })
      });

      if (response.ok) {
        setPlatforms(updatedPlatforms);
        toast.success('Configuração salva!');
        setConfigDialogOpen(false);
        checkPlatformStatus();
      } else {
        toast.error('Erro ao salvar configuração');
      }
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };

  const openPlatform = (platform) => {
    if (platform.isCurrentPlatform) {
      navigate(platform.adminPath);
    } else if (platform.url) {
      window.open(platform.url + platform.adminPath, '_blank');
    } else {
      setSelectedPlatform(platform);
      setConfigDialogOpen(true);
    }
  };

  const getStatusBadge = (platformId, platform) => {
    if (platform.isCurrentPlatform) {
      return <Badge className="bg-emerald-100 text-emerald-700 border-0">Ativo</Badge>;
    }
    if (platform.url) {
      return <Badge className="bg-blue-100 text-blue-700 border-0">Configurado</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600 border-0">Não configurado</Badge>;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="portal-g2">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <span className="text-white font-bold text-xl">G2</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white font-['Outfit']">Portal G2</h1>
                <p className="text-slate-400 text-sm">Central de Administração</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/plataforma/chat')}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Mensagens
                {stats.mensagensNaoLidas > 0 && (
                  <Badge className="ml-2 bg-red-500 text-white">{stats.mensagensNaoLidas}</Badge>
                )}
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/plataforma')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Jornyx Admin
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <div className="text-right mr-2">
                <p className="text-sm text-white font-medium">{user?.nome}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          <Card className="bg-white/10 backdrop-blur border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.totalEmpresas}</p>
                  <p className="text-sm text-slate-400">Empresas Jornyx</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/10 backdrop-blur border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">3</p>
                  <p className="text-sm text-slate-400">Plataformas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/10 backdrop-blur border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {platforms.filter(p => platformStatus[p.id] === 'online').length}
                  </p>
                  <p className="text-sm text-slate-400">Online</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/10 backdrop-blur border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.mensagensNaoLidas}</p>
                  <p className="text-sm text-slate-400">Mensagens</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platforms Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-6 font-['Outfit']">
            Suas Plataformas
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {platforms.map((platform) => {
              const Icon = platform.icon;
              return (
                <Card
                  key={platform.id}
                  className="group bg-white/5 backdrop-blur border-white/10 hover:bg-white/10 transition-all duration-300 overflow-hidden cursor-pointer"
                  onClick={() => openPlatform(platform)}
                  data-testid={`platform-card-${platform.id}`}
                >
                  {/* Gradient Header */}
                  <div className={`h-2 bg-gradient-to-r ${platform.color}`} />
                  
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 ${platform.bgColor} rounded-xl flex items-center justify-center`}>
                        <Icon className={`w-7 h-7 ${platform.iconColor}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(platform.id, platform)}
                        {!platform.isCurrentPlatform && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlatform(platform);
                              setConfigDialogOpen(true);
                            }}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-1 font-['Outfit']">
                      {platform.name}
                    </h3>
                    <p className="text-slate-400 text-sm mb-4">{platform.description}</p>

                    {/* Features List */}
                    <ul className="space-y-2 mb-6">
                      {platform.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* Action */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <span className="text-sm text-slate-400">
                        {platform.isCurrentPlatform ? 'Plataforma atual' : platform.url ? 'Conectado' : 'Configurar URL'}
                      </span>
                      <div className="flex items-center gap-1 text-emerald-400 group-hover:translate-x-1 transition-transform">
                        <span className="text-sm font-medium">Acessar</span>
                        {platform.isCurrentPlatform ? (
                          <ChevronRight className="w-4 h-4" />
                        ) : (
                          <ExternalLink className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-white mb-6 font-['Outfit']">
            Ações Rápidas - Jornyx
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Empresas', icon: Building2, path: '/plataforma/empresas' },
              { label: 'Planos', icon: BarChart3, path: '/plataforma/planos' },
              { label: 'Mensagens', icon: MessageCircle, path: '/plataforma/chat', badge: stats.mensagensNaoLidas },
              { label: 'Configurações', icon: Settings, path: '/plataforma/configuracoes' }
            ].map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                className="h-auto py-4 bg-white/5 hover:bg-white/10 border border-white/10 flex flex-col items-center gap-2 text-slate-300 hover:text-white"
                onClick={() => navigate(item.path)}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
                {item.badge > 0 && (
                  <Badge className="bg-red-500 text-white">{item.badge}</Badge>
                )}
              </Button>
            ))}
          </div>
        </div>
      </main>

      {/* Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              {selectedPlatform && (
                <>
                  <selectedPlatform.icon className={`w-5 h-5 ${selectedPlatform.iconColor}`} />
                  Configurar {selectedPlatform.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure a URL de acesso ao painel administrativo da plataforma.
            </DialogDescription>
          </DialogHeader>

          {selectedPlatform && (
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="platform-url" className="text-slate-300">URL do Sistema</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="platform-url"
                      placeholder="https://sistema.exemplo.com.br"
                      defaultValue={selectedPlatform.url}
                      className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  O caminho do admin ({selectedPlatform.adminPath}) será adicionado automaticamente.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="ghost"
                  onClick={() => setConfigDialogOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    const url = document.getElementById('platform-url').value;
                    savePlatformConfig(selectedPlatform.id, url);
                  }}
                >
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalG2;
